import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../utils/axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { StatCard } from './components/StatCard';
import { KpiTimeChart } from './components/KpiTimeChart';
import { SectionExportButton } from './components/SectionExportButton';
import { GlosarioHelpButton } from './components/GlosarioHelpButton';
import { SectionStatusChip } from './components/SectionStatusChip';
import { fmtNum, fmtNumShort, fmtPeriodo, calcDelta } from './components/kpiUtils';
import { UserCheck, UserX, WalletCards, PiggyBank, Upload, Activity, ReceiptText, Users, Wallet, TrendingUp, UserPlus, UserMinus, Repeat, ArrowRight, User, Building2, Coins } from 'lucide-react';

const C_MAU = '#4f46e5';
const C_PAGO = '#0ea5e9';
const C_PREST = '#8b5cf6';
const C_RET = '#10b981';
const C_NUEVO = '#0ea5e9';
const C_PERD = '#f43f5e';

const nowMonth = () => new Date().toISOString().slice(0, 7);
const pct = (n, t) => (t ? Math.round((n / t) * 100) : 0);

const Barra = ({ items, testId }) => {
  const total = items.reduce((s, i) => s + i.v, 0) || 1;
  return (
    <div data-testid={testId}>
      <div className="flex h-2.5 w-full rounded-full overflow-hidden bg-slate-100 dark:bg-slate-800">
        {items.map((i) => <div key={i.label} style={{ width: `${(i.v / total) * 100}%`, background: i.color }} title={`${i.label}: ${i.v}`} />)}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2.5">
        {items.map((i) => (
          <div key={i.label} className="flex items-center gap-1.5 text-sm">
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: i.color }} />
            <span className="text-slate-600 dark:text-slate-300">{i.label}</span>
            <span className="font-semibold tabular-nums text-slate-800 dark:text-slate-100">{fmtNum(i.v)}</span>
            <span className="text-xs text-slate-400">({pct(i.v, total)}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const KpisClientesActivos = () => {
  const [data, setData] = useState({ periodos: [], periodo: null, snapshot: null, mau: [], puede_cargar: false });
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [periodoUp, setPeriodoUp] = useState(nowMonth());
  const [mainView, setMainView] = useState('mau');
  const [comp, setComp] = useState(null);
  const [cmpLoading, setCmpLoading] = useState(false);
  const [overwriteFile, setOverwriteFile] = useState(null);
  const fileRef = useRef(null);
  const rootRef = useRef(null);

  const load = async (per) => {
    try {
      const r = await api.get('/kpis/billeteras', { params: per ? { periodo: per } : {} });
      setData(r.data);
      return r.data;
    } catch (e) { toast.error('Error al cargar Clientes activos'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const pollStatus = async (per, tries = 0) => {
    if (tries > 60) return;
    const d = await load(per);
    const st = d?.snapshot?.mau_status;
    if (st === 'procesando') { setTimeout(() => pollStatus(per, tries + 1), 3000); }
    else if (st === 'listo') { toast.success('MAU calculado ✔'); if (mainView === 'retencion') loadComp(); }
    else if (st === 'error') { toast.error('Hubo un error calculando el MAU'); }
  };

  const loadComp = async (base, actual) => {
    setCmpLoading(true);
    try {
      const r = await api.get('/kpis/billeteras/comparar', { params: { base: base || undefined, actual: actual || undefined } });
      setComp(r.data);
    } catch (e) { toast.error('Error al cargar la comparación'); }
    finally { setCmpLoading(false); }
  };
  useEffect(() => { if (mainView === 'retencion' && !comp) loadComp(); }, [mainView]); // eslint-disable-line

  const { periodos, periodo, snapshot, mau, puede_cargar } = data;
  const totals = snapshot?.totals || {};
  const demo = totals.demografia;

  const doUpload = async (file) => {
    setImporting(true);
    try {
      const fd = new FormData(); fd.append('file', file); fd.append('periodo', periodoUp);
      const r = await api.post('/kpis/billeteras/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 });
      toast.success(`${fmtPeriodo(r.data.periodo)}: ${fmtNum(r.data.activos)} activos cargados. Calculando MAU en segundo plano…`);
      setComp(null);
      await load(periodoUp);
      pollStatus(periodoUp);
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo importar el archivo'); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const onPick = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!/^\d{4}-\d{2}$/.test(periodoUp)) { toast.error('Elegí el mes del reporte'); return; }
    if ((periodos || []).includes(periodoUp)) { setOverwriteFile(file); return; }  // pedir confirmación
    doUpload(file);
  };
  const confirmarReemplazo = () => { const f = overwriteFile; setOverwriteFile(null); if (f) doUpload(f); };
  const cancelarReemplazo = () => { setOverwriteFile(null); if (fileRef.current) fileRef.current.value = ''; };

  const eliminar = async () => {
    if (!periodo) return;
    if (!window.confirm(`¿Eliminar el reporte de ${fmtPeriodo(periodo)}?`)) return;
    try { await api.delete(`/kpis/billeteras/${periodo}`); toast.success('Reporte eliminado'); setComp(null); load(); }
    catch (e) { toast.error('No se pudo eliminar'); }
  };

  const chartRows = useMemo(() => (mau || []).map((m) => ({
    label: fmtPeriodo(m.mes, true), mau: m.mau, con_pago: m.con_pago, con_prestamo: m.con_prestamo, remuneradas: m.remuneradas,
  })), [mau]);
  const mesRow = useMemo(() => (mau || []).find((m) => m.mes === periodo) || (mau || [])[mau?.length - 1] || {}, [mau, periodo]);

  const c = comp?.comparacion;
  const cmpBars = c ? [{ label: 'Retenidos', v: c.retenidos }, { label: 'Nuevos', v: c.nuevos }, { label: 'Perdidos', v: c.perdidos }] : [];

  if (loading) return <div className="p-8 text-slate-500" data-testid="ca-loading">Cargando Clientes activos…</div>;

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" ref={rootRef} data-testid="kpis-clientes-activos">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-indigo-500/10"><UserCheck className="h-6 w-6 text-indigo-600" /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Clientes activos</h1>
            <div className="mt-1.5"><SectionStatusChip seccionKey="kpis-clientes-activos" /></div>
            <p className="text-sm text-slate-500">MAU y retención a partir del Reporte de billeteras, cruzado con movimientos</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <GlosarioHelpButton seccion="clientes" testId="ca-help-btn" />
          <SectionExportButton title="Clientes activos" targetRef={rootRef} testId="ca-export-btn" />
          {puede_cargar && (
            <>
              <input type="month" value={periodoUp} onChange={(e) => setPeriodoUp(e.target.value)}
                className="h-9 rounded-md border border-slate-300 dark:border-slate-700 bg-transparent px-2 text-sm" data-testid="ca-periodo-input" />
              <Button onClick={() => fileRef.current?.click()} disabled={importing}
                className="bg-indigo-600 hover:bg-indigo-700 active:scale-95 transition-transform" data-testid="ca-subir-btn">
                <Upload className="h-4 w-4 mr-1" /> {importing ? 'Procesando…' : 'Subir reporte'}
              </Button>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={onPick} data-testid="ca-file-input" />
            </>
          )}
        </div>
      </div>

      {/* View toggle (shadcn Tabs) */}
      <Tabs value={mainView} onValueChange={setMainView} className="mb-6">
        <TabsList data-testid="ca-view-tabs">
          <TabsTrigger value="mau" data-testid="ca-view-mau"><Activity className="h-4 w-4 mr-1.5" /> MAU (actividad)</TabsTrigger>
          <TabsTrigger value="retencion" data-testid="ca-view-retencion"><TrendingUp className="h-4 w-4 mr-1.5" /> Retención / Crecimiento</TabsTrigger>
        </TabsList>
      </Tabs>

      {snapshot?.mau_status === 'procesando' && (
        <div className="mb-5 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-500/10 px-4 py-2.5 text-sm text-amber-700 dark:text-amber-400" data-testid="ca-mau-procesando">
          <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
          Calculando el MAU en segundo plano… (la foto y la retención ya están disponibles)
        </div>
      )}

      {!snapshot ? (
        <Card className="p-10 text-center" data-testid="ca-empty">
          <WalletCards className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-600 dark:text-slate-300 font-medium">Todavía no cargaste ningún Reporte de billeteras</p>
          <p className="text-sm text-slate-400 mt-1">{puede_cargar ? 'Elegí el mes y subí el Excel exportado de Waya (ReporteBilleteras).' : 'Pedile a un administrador que cargue el reporte.'}</p>
        </Card>
      ) : mainView === 'mau' ? (
        <>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400">Foto del reporte</h2>
              {periodos?.length > 0 && (
                <Select value={periodo || undefined} onValueChange={(v) => load(v)}>
                  <SelectTrigger className="w-[140px] h-8" data-testid="ca-periodo-select"><SelectValue placeholder="Período" /></SelectTrigger>
                  <SelectContent>
                    {periodos.map((p) => <SelectItem key={p} value={p} data-testid={`ca-periodo-opt-${p}`}>{fmtPeriodo(p)}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            {puede_cargar && <button onClick={eliminar} className="text-xs text-rose-500 hover:underline" data-testid="ca-eliminar-btn">Eliminar reporte</button>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <StatCard label="Clientes habilitados" value={fmtNum(totals.activos)} icon={UserCheck} accent="#4f46e5" deltaLabel="con CVU + Habilitado" testId="ca-kpi-activos" />
            <StatCard label="Bloqueados" value={fmtNum(totals.bloqueados)} icon={UserX} accent="#f43f5e" deltaLabel="con CVU + Deshabilitado" testId="ca-kpi-bloqueados" />
            <StatCard label="Saldo ≥ $8.000" value={fmtNum(totals.con_saldo_8000)} icon={PiggyBank} accent="#10b981" deltaLabel="de los activos" testId="ca-kpi-saldo" />
          </div>

          {/* Composición demográfica: dos tarjetas separadas */}
          {demo && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <Card className="p-5 lg:p-6" data-testid="ca-demografia-tipo">
                <div className="flex items-center gap-2 mb-1 text-base font-semibold text-slate-800 dark:text-slate-200"><Building2 className="h-4 w-4 text-indigo-600" /> Por tipo de persona</div>
                <p className="text-xs text-slate-400 mb-5">Física = CUIT 20/23/24/27 · Jurídica = 30/33/34.</p>
                <Barra testId="ca-demo-tipo" items={[
                  { label: 'Física', v: demo.tipo?.fisica || 0, color: '#4f46e5' },
                  { label: 'Jurídica', v: demo.tipo?.juridica || 0, color: '#0ea5e9' },
                  ...(demo.tipo?.otro ? [{ label: 'Otro', v: demo.tipo.otro, color: '#94a3b8' }] : []),
                ]} />
              </Card>
              <Card className="p-5 lg:p-6" data-testid="ca-demografia-genero">
                <div className="flex items-center gap-2 mb-1 text-base font-semibold text-slate-800 dark:text-slate-200"><User className="h-4 w-4 text-indigo-600" /> Por género (personas físicas)</div>
                <p className="text-xs text-slate-400 mb-5">Inferido del prefijo del CUIT (20=masc., 27=fem., 23/24=indistinto).</p>
                <Barra testId="ca-demo-genero" items={[
                  { label: 'Masculino', v: demo.genero?.masculino || 0, color: '#3b82f6' },
                  { label: 'Femenino', v: demo.genero?.femenino || 0, color: '#ec4899' },
                  { label: 'Indistinto', v: demo.genero?.indistinto || 0, color: '#a78bfa' },
                ]} />
              </Card>
            </div>
          )}

          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-2">Actividad de {fmtPeriodo(mesRow.mes || periodo)} (agregado, sin datos personales)</h2>
          <p className="text-xs text-slate-400 mb-3" data-testid="ca-jerarquia-nota">
            El MAU contempla todas las cuentas activas del mes. El mayor grado de actividad son las <span className="font-medium text-slate-500 dark:text-slate-300">cuentas remuneradas</span> (mantienen saldo generando remuneración — último valor cargado del mes); luego las que registran <span className="font-medium text-slate-500 dark:text-slate-300">movimientos</span>, y por último el resto de los activos.
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <StatCard label="Cuentas remuneradas" value={mesRow.remuneradas == null ? '—' : fmtNum(mesRow.remuneradas)} icon={Coins} accent="#14b8a6" deltaLabel={mesRow.remuneradas == null ? 'cargá Cuenta Remunerada' : 'activas por saldo remunerado'} testId="ca-kpi-remuneradas" />
            <StatCard label="Con movimientos (MAU)" value={fmtNum(mesRow.mau)} icon={Activity} accent="#4f46e5" deltaLabel={`de ${fmtNum(totals.activos)} activos`} testId="ca-kpi-mau" />
            <StatCard label="Con pago de servicio" value={fmtNum(mesRow.con_pago)} icon={ReceiptText} accent="#0ea5e9" testId="ca-kpi-pago" />
            <StatCard label="Con préstamo" value={fmtNum(mesRow.con_prestamo)} icon={Wallet} accent="#8b5cf6" testId="ca-kpi-prestamo" />
            <StatCard label="Con pago y préstamo" value={mesRow.con_ambas == null ? '—' : fmtNum(mesRow.con_ambas)} icon={Activity} accent="#f59e0b" deltaLabel={mesRow.con_ambas == null ? 'reprocesá el reporte de billeteras' : (mesRow.con_ambas_saldo != null ? `${fmtNum(mesRow.con_ambas_saldo)} además con saldo ≥ $8.000` : 'ambas señales en el mes')} testId="ca-kpi-senal" />
          </div>

          <Card className="p-5 lg:p-6 mb-6" data-testid="ca-chart-card">
            <h2 className="text-lg font-semibold tracking-tight text-slate-800 dark:text-slate-200 mb-5">Evolución mensual del MAU</h2>
            <KpiTimeChart data={chartRows} type="linea"
              series={[
                { key: 'remuneradas', name: 'Remuneradas', color: '#14b8a6' },
                { key: 'mau', name: 'Activos (MAU)', color: C_MAU },
                { key: 'con_pago', name: 'Pago de servicio', color: C_PAGO },
                { key: 'con_prestamo', name: 'Préstamo', color: C_PREST },
              ]}
              valueFormatter={fmtNum} axisFormatter={fmtNumShort} height={340} testId="ca-chart" />
            {chartRows.length === 0 && <p className="text-sm text-slate-400 text-center py-8">Sin movimientos cruzados para este reporte.</p>}
          </Card>

          <Card className="p-0 overflow-hidden" data-testid="ca-tabla-mau">
            <div className="p-5 border-b border-slate-100 dark:border-slate-800">
              <h2 className="text-base font-semibold tracking-tight text-slate-800 dark:text-slate-200">Detalle mensual</h2>
              <p className="text-xs text-slate-400 mt-1">El MAU refleja los clientes activos que tienen movimientos en nuestra base. El saldo ≥ $8.000 sólo se conoce del mes del reporte.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 dark:bg-slate-800 text-slate-500">
                    <th className="text-left font-medium px-4 py-2.5">Mes</th>
                    <th className="text-right font-medium px-4 py-2.5">Remuneradas</th>
                    <th className="text-right font-medium px-4 py-2.5">Activos (MAU)</th>
                    <th className="text-right font-medium px-4 py-2.5">Pago de servicio</th>
                    <th className="text-right font-medium px-4 py-2.5">Préstamo</th>
                    <th className="text-right font-medium px-4 py-2.5">Pago y préstamo</th>
                    <th className="text-right font-medium px-4 py-2.5">Saldo ≥ $8.000</th>
                  </tr>
                </thead>
                <tbody>
                  {[...(mau || [])].reverse().map((m) => (
                    <tr key={m.mes} className="border-t border-slate-100 dark:border-slate-800" data-testid={`ca-row-${m.mes}`}>
                      <td className="px-4 py-2.5 font-medium text-slate-700 dark:text-slate-200">{fmtPeriodo(m.mes)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-teal-600 dark:text-teal-400 font-semibold">{m.remuneradas == null ? '—' : fmtNum(m.remuneradas)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-semibold">{fmtNum(m.mau)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{fmtNum(m.con_pago)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{fmtNum(m.con_prestamo)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{m.con_ambas == null ? fmtNum(m.con_senal) : fmtNum(m.con_ambas)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{m.con_saldo_8000 == null ? '—' : fmtNum(m.con_saldo_8000)}</td>
                    </tr>
                  ))}
                  {(mau || []).length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No hay datos mensuales.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      ) : (
        /* ---------------- RETENCIÓN / CRECIMIENTO ---------------- */
        <div data-testid="ca-retencion">
          {comp && (
          <Card className="p-4 lg:p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap" data-testid="ca-cmp-selectores">
            <span className="text-sm font-medium text-slate-500">Comparar</span>
            <div className="flex items-center gap-1.5">
              <span className="text-xs uppercase text-slate-400">Base</span>
              <Select value={comp?.base || undefined} onValueChange={(v) => loadComp(v, comp?.actual)}>
                <SelectTrigger className="w-[140px] h-9" data-testid="ca-cmp-base-select"><SelectValue placeholder="Base" /></SelectTrigger>
                <SelectContent>{(comp?.periodos || periodos || []).map((p) => <SelectItem key={p} value={p}>{fmtPeriodo(p)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <ArrowRight className="h-4 w-4 text-slate-400" />
            <div className="flex items-center gap-1.5">
              <span className="text-xs uppercase text-slate-400">Actual</span>
              <Select value={comp?.actual || undefined} onValueChange={(v) => loadComp(comp?.base, v)}>
                <SelectTrigger className="w-[140px] h-9" data-testid="ca-cmp-actual-select"><SelectValue placeholder="Actual" /></SelectTrigger>
                <SelectContent>{(comp?.periodos || periodos || []).map((p) => <SelectItem key={p} value={p}>{fmtPeriodo(p)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </Card>
          )}

          {cmpLoading ? (
            <div className="p-8 text-slate-400 text-sm" data-testid="ca-cmp-loading">Calculando comparación…</div>
          ) : !c || (comp?.periodos || []).length < 2 ? (
            <Card className="p-10 text-center" data-testid="ca-cmp-empty">
              <TrendingUp className="h-10 w-10 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-600 dark:text-slate-300 font-medium">Necesitás al menos dos reportes cargados para comparar</p>
              <p className="text-sm text-slate-400 mt-1">Subí un segundo período (ej. el reporte "a hoy") para ver la retención y el crecimiento.</p>
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <StatCard label={`Activos ${fmtPeriodo(comp.base)}`} value={fmtNum(c.base_activos)} icon={Users} accent="#64748b" testId="ca-cmp-kpi-base" />
                <StatCard label={`Activos ${fmtPeriodo(comp.actual)}`} value={fmtNum(c.actual_activos)} icon={UserCheck} accent="#4f46e5" delta={calcDelta(c.actual_activos, c.base_activos)} testId="ca-cmp-kpi-actual" />
                <StatCard label="Crecimiento neto" value={`${c.crecimiento_neto >= 0 ? '+' : ''}${fmtNum(c.crecimiento_neto)}`} icon={TrendingUp} accent={c.crecimiento_neto >= 0 ? '#10b981' : '#f43f5e'} deltaLabel={c.crecimiento_pct != null ? `${c.crecimiento_pct >= 0 ? '+' : ''}${c.crecimiento_pct}%` : ''} testId="ca-cmp-kpi-crecimiento" />
                <StatCard label="Retención" value={c.retencion_pct != null ? `${c.retencion_pct}%` : '—'} icon={Repeat} accent="#0ea5e9" deltaLabel={`${fmtNum(c.retenidos)} retenidos`} testId="ca-cmp-kpi-retencion" />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                <Card className="p-5 lg:p-6 lg:col-span-2" data-testid="ca-cmp-chart-card">
                  <h2 className="text-lg font-semibold tracking-tight text-slate-800 dark:text-slate-200 mb-5">Retenidos · Nuevos · Perdidos</h2>
                  <KpiTimeChart data={cmpBars} xKey="label" type="barras"
                    series={[{ key: 'v', name: 'Clientes', color: C_RET }]}
                    cellColors={[C_RET, C_NUEVO, C_PERD]} showLegend={false}
                    valueFormatter={fmtNum} axisFormatter={fmtNumShort} height={280} testId="ca-cmp-chart" />
                </Card>
                <div className="grid grid-cols-1 gap-4">
                  <StatCard label="Retenidos" value={fmtNum(c.retenidos)} icon={Repeat} accent={C_RET} deltaLabel={`${c.retencion_pct}% de la base`} testId="ca-cmp-retenidos" />
                  <StatCard label="Nuevos" value={fmtNum(c.nuevos)} icon={UserPlus} accent={C_NUEVO} testId="ca-cmp-nuevos" />
                  <StatCard label="Perdidos" value={fmtNum(c.perdidos)} icon={UserMinus} accent={C_PERD} testId="ca-cmp-perdidos" />
                </div>
              </div>

              <Card className="p-5 lg:p-6" data-testid="ca-cmp-contexto">
                <h2 className="text-base font-semibold tracking-tight text-slate-800 dark:text-slate-200 mb-4">Evolución de la base ({fmtPeriodo(comp.base)} → {fmtPeriodo(comp.actual)})</h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { k: 'con_cvu', label: 'Total con CVU', color: '#6366f1', tid: 'ca-cmp-concvu' },
                    { k: 'bloqueados', label: 'Bloqueados', color: '#f43f5e', tid: 'ca-cmp-bloqueados' },
                    { k: 'sin_cvu', label: 'Sin CVU', color: '#64748b', tid: 'ca-cmp-sincvu' },
                    { k: 'con_saldo_8000', label: 'Saldo ≥ $8.000', color: '#10b981', tid: 'ca-cmp-saldo' },
                  ].map(({ k, label, color, tid }) => {
                    const vb = c.totales_base[k], va = c.totales_actual[k];
                    return (
                      <div key={k} className="rounded-xl border border-slate-200 dark:border-slate-800 p-4" data-testid={tid}>
                        <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</p>
                        <p className="text-xl font-bold mt-1.5 tabular-nums" style={{ color }}>
                          {fmtNum(vb)} <ArrowRight className="inline h-4 w-4 text-slate-300 mx-0.5" /> {fmtNum(va)}
                        </p>
                        <p className="text-xs mt-1 text-slate-400 tabular-nums">{va - vb >= 0 ? '+' : ''}{fmtNum(va - vb)}</p>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </>
          )}
        </div>
      )}

      {/* Aviso de reemplazo de período existente */}
      <AlertDialog open={!!overwriteFile} onOpenChange={(o) => { if (!o) cancelarReemplazo(); }}>
        <AlertDialogContent data-testid="ca-overwrite-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Ya existe un reporte de {fmtPeriodo(periodoUp)}</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a reemplazar los datos cargados para {fmtPeriodo(periodoUp)} (foto, MAU y registros sin CVU). Esta acción sobrescribe el reporte anterior de ese mes. ¿Querés continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelarReemplazo} data-testid="ca-overwrite-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmarReemplazo} className="bg-indigo-600 hover:bg-indigo-700" data-testid="ca-overwrite-confirm">Reemplazar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default KpisClientesActivos;
